import {
  ApolloClient,
  gql,
  HttpLink,
  InMemoryCache,
  useMutation,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "@laborhack/custom-button";
import React, { FC, useEffect, useState } from "react";
import { Form } from "semantic-ui-react";
import {
  addPhoneNumberPrefix,
  nonEmpty,
  phoneNumberCheck,
} from "../../../../helpers/utils/funtions";
import { FieldValue } from "../../../../types";
import CustomInput from "@laborhack/input";
import Loading from "../../../../_components/Loading";
import RequestFailed from "../../../../_components/RequestFailed";
import { CLAIMS_NAMESPACE } from "../../../../constants";

const SAVE_BUSINESS = gql`
  mutation SaveBusiness($payload: SaveBusinessInput!) {
    saveBusiness(input: $payload) {
      id
      name
      clients {
        id
        firstName
        phoneNumber
      }
    }
  }
`;

export interface BusinessSignUpFormProps {}

export const BusinessSignUpForm: FC<BusinessSignUpFormProps> = ({}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    getIdTokenClaims,
  } = useAuth0();

  const [getClaimsLoading, setGetClaimsLoading] = useState(true);

  const httpLink = new HttpLink({
    uri: `${process.env.REACT_APP_BACKEND_URL}/graphql`,
  });

  const authLink = setContext((_, { headers }) => {
    return new Promise(async (resolve) => {
      const token = await getAccessTokenSilently();

      resolve({
        headers: {
          ...headers,
          authorization: token,
          authprovider: "auth0",
        },
      });
    });
  });

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: authLink.concat(httpLink),
  });

  const [saveBusiness, { loading, error, reset }] = useMutation(SAVE_BUSINESS, {
    client,
    onCompleted: () => {
      window.open(process.env.REACT_APP_MARKETPLACE_URL, "_self");
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      getIdTokenClaims().then((claims) => {
        const roles = claims?.[`${CLAIMS_NAMESPACE}/roles`];

        if (roles?.includes("CLIENT")) {
          window.open(process.env.REACT_APP_MARKETPLACE_URL, "_self");
        }

        setGetClaimsLoading(false);
      });
    }
  }, [isAuthenticated]);

  const defaultField = {
    value: "",
    isValid: false,
  };

  /**
   * Company Details
   */
  const [companyName, setCompanyName] =
    useState<FieldValue<string>>(defaultField);
  const [companyStreet, setCompanyStreet] =
    useState<FieldValue<string>>(defaultField);
  const [companyCity, setCompanyCity] =
    useState<FieldValue<string>>(defaultField);
  const [companyState, setCompanyState] =
    useState<FieldValue<string>>(defaultField);

  /**
   * Contact Details
   */

  const [firstName, setFirstName] = useState<FieldValue<string>>(defaultField);
  const [lastName, setLastName] = useState<FieldValue<string>>(defaultField);
  const [phoneNumber, setPhoneNumber] =
    useState<FieldValue<string>>(defaultField);

  const createAccountEnabled =
    companyName.isValid &&
    firstName.isValid &&
    lastName.isValid &&
    phoneNumber.isValid;

  if (loading || isLoading || getClaimsLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <RequestFailed
        text={error.message}
        onRetry={() => {
          reset();
        }}
      />
    );
  }

  const handleSave = () => {
    saveBusiness({
      variables: {
        payload: {
          name: companyName.value,
          address: companyStreet.value,
          city: companyCity.value,
          state: companyState.value,
          clients: [
            {
              firstName: firstName.value,
              lastName: lastName.value,
              phoneNumber: addPhoneNumberPrefix(phoneNumber.value),
            },
          ],
        },
      },
    });
  };

  return (
    <Form>
      <div>
        <h3 className="text-primary-500 font-bold">Company Details</h3>
        <CustomInput
          autoComplete="name"
          required
          textLabel="Company Name"
          value={companyName.value}
          onChange={(value: string, isValid: boolean) => {
            setCompanyName({ value, isValid });
          }}
          validate={{
            check: nonEmpty,
            message: "Invalid company name",
          }}
          placeholder="Company Name"
        />
        <CustomInput
          autoComplete="street-address"
          textLabel="Company Address"
          value={companyStreet.value}
          onChange={(value: string, isValid: boolean) => {
            setCompanyStreet({ value, isValid });
          }}
          placeholder="Address"
        />
        <Form.Group widths="equal">
          <CustomInput
            autoComplete="city"
            textLabel="Company City"
            value={companyCity.value}
            onChange={(value: string, isValid: boolean) => {
              setCompanyCity({ value, isValid });
            }}
            placeholder="City"
          />
          <CustomInput
            autoComplete="state"
            textLabel="Company State"
            value={companyState.value}
            onChange={(value: string, isValid: boolean) => {
              setCompanyState({ value, isValid });
            }}
            placeholder="State"
          />
        </Form.Group>
      </div>
      <div className="mt-12 mb-8">
        <h3 className="text-primary-500 font-bold">Contact Details</h3>

        <Form.Group widths="equal">
          <CustomInput
            required
            textLabel="First Name"
            value={firstName.value}
            onChange={(value: string, isValid: boolean) => {
              setFirstName({ value, isValid });
            }}
            validate={{
              check: nonEmpty,
              message: "Invalid Name",
            }}
            placeholder="First Name"
          />
          <CustomInput
            required
            textLabel="Last Name"
            value={lastName.value}
            onChange={(value: string, isValid: boolean) => {
              setLastName({ value, isValid });
            }}
            validate={{
              check: nonEmpty,
              message: "Invalid Name",
            }}
            placeholder="Last Name"
          />
        </Form.Group>
        <CustomInput
          autoComplete="email"
          type="email"
          required
          textLabel="Email Address"
          value={user?.email!}
          disabled={true}
          onChange={(value: string, isValid: boolean) => {}}
          placeholder="Enter Email Address"
        />
        <CustomInput
          autoComplete="tel"
          required
          textLabel="Phone Number"
          label="+234"
          type="tel"
          value={phoneNumber.value}
          onChange={(value: string, isValid: boolean) => {
            setPhoneNumber({ value, isValid });
          }}
          validate={{
            check: phoneNumberCheck,
            message: "Invalid Phone Number",
          }}
          placeholder="8000000000"
        />
      </div>
      <Button
        size="big"
        type="circular"
        loading={loading}
        disabled={!createAccountEnabled}
        onClick={handleSave}
        className="w-full"
      >
        Join LaborHack
      </Button>
    </Form>
  );
};
